import { DomainError } from "./errors";
import { orderCommissions } from "./commission";
import {
  MAX_QUOTE_ITEMS,
  quoteTotals,
  type QuoteLine,
  type QuoteLineInput,
  type SalesOrderDraft,
} from "./quotes";
import { planEffects, type BaseState, type EffectState, type EffectStep } from "./status-flow";

// ===== Pedidos de venda (RF-VEN-02; 05 §2, §3, §5 e §6) =====

/** Itens por pedido (como a proposta: a tela e o PDF mostram todos). */
export const MAX_SALES_ORDER_ITEMS = MAX_QUOTE_ITEMS;
/** Pedidos por requisição nas ações em lote (cada um na própria transação do DO). */
export const MAX_BULK_ORDERS = 50;
/**
 * Linhas de estoque (depois de expandir os kits, mais reservas liberadas e movimentos estornados) somadas no lote de
 * situação inteiro. Cada pedido já vai até MAX_DOC_STOCK_LINES; sem o teto do lote, 50 pedidos fariam 100 mil
 * reservas/movimentos numa requisição só.
 */
export const MAX_BULK_STOCK_LINES = 5_000;

export type StockBudget = {
  /** Consome `n` linhas; passar do teto lança `bulk_too_large` sem consumir nada. */
  use(n: number): void;
  readonly used: number;
  readonly remaining: number;
};

/** Orçamento de linhas de estoque de um lote (o motor consome antes de reservar, lançar, liberar ou estornar). */
export function stockBudget(max: number = MAX_BULK_STOCK_LINES): StockBudget {
  let used = 0;
  return {
    use(n: number) {
      if (used + n > max)
        fail(
          "bulk_too_large",
          `O lote passa de ${max.toLocaleString("pt-BR")} itens de estoque (contando os componentes dos kits). Este pedido não foi alterado; ` +
            "repita com menos pedidos.",
        );
      used += n;
    },
    get used() {
      return used;
    },
    get remaining() {
      return max - used;
    },
  };
}

/** Ações por pedido fora de uma transição (PRD RF-VEN-02: lançar/estornar estoque e contas). */
export const ORDER_MANUAL_ACTIONS = [
  "post_stock",
  "reverse_stock",
  "post_financial",
  "reverse_financial",
] as const;
export type OrderManualAction = (typeof ORDER_MANUAL_ACTIONS)[number];
export const ORDER_MANUAL_ACTION_LABELS: Readonly<Record<OrderManualAction, string>> = {
  post_stock: "Lançar estoque",
  reverse_stock: "Estornar estoque",
  post_financial: "Lançar contas",
  reverse_financial: "Estornar contas",
};

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

export type OrderCalcInput = {
  items: readonly QuoteLineInput[];
  discountCents: number;
  /** Desconto geral em % sobre os itens; null = em valor. */
  discountPct4: number | null;
  freightCents: number;
  otherExpensesCents: number;
};
export type OrderTotals = {
  lines: QuoteLine[];
  itemsTotalCents: number;
  itemsDiscountCents: number;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
};

/**
 * Totais do pedido (05 §3) com a conta da proposta (T1.7): no pedido todo item, cadastrado ou avulso, soma em
 * "itens". Mesmos tetos (200 itens, R$ 1 trilhão, desconto geral até o subtotal).
 */
export function salesOrderTotals(i: OrderCalcInput): OrderTotals {
  let t: ReturnType<typeof quoteTotals>;
  try {
    t = quoteTotals({ ...i, items: i.items.map((it) => ({ ...it, kind: "product" as const })) });
  } catch (e) {
    // A conta é a da proposta; a mensagem do teto fala do pedido.
    if (e instanceof DomainError && e.code === "total_limit")
      fail("total_limit", "Total do pedido acima do limite");
    throw e;
  }
  return {
    lines: t.lines,
    itemsTotalCents: t.itemsTotalCents,
    itemsDiscountCents: t.itemsDiscountCents,
    discountCents: t.discountCents,
    freightCents: t.freightCents,
    otherExpensesCents: t.otherExpensesCents,
    totalCents: t.totalCents,
    itemsCount: t.itemsCount,
    quantitySumQ4: t.quantitySumQ4,
  };
}

/**
 * Comissão de cada item (05 §6): `pct4` vem da precedência das regras (`resolveCommission`); null = item sem vendedor
 * (fica sem comissão). O desconto geral do pedido é rateado pelo total dos itens antes do percentual.
 */
export function orderItemCommissions(
  items: readonly { totalCents: number; pct4: number | null }[],
  orderDiscountCents: number,
): { items: ({ pct4: number; cents: number } | null)[]; totalCents: number } {
  const r = orderCommissions(
    items.map((it) => ({ totalCents: it.totalCents, pct4: it.pct4 ?? 0 })),
    orderDiscountCents,
  );
  return {
    items: items.map((it, k) =>
      it.pct4 === null ? null : { pct4: it.pct4, cents: r.items[k]!.commissionCents },
    ),
    totalCents: r.totalCents,
  };
}

/**
 * Por que o pedido não pode ser editado nem excluído (null = pode). Itens e valores ficam presos enquanto houver
 * estoque reservado ou lançado ou contas lançadas (os efeitos foram feitos com os itens de antes) e em situação
 * concluída ou cancelada.
 */
export function orderLockProblem(effects: EffectState, base: BaseState): string | null {
  if (effects.stock === "reserved")
    return 'O pedido tem estoque reservado. Volte a situação para "Em aberto" antes de alterar.';
  if (effects.stock === "posted") return "O pedido tem estoque lançado. Estorne o estoque antes de alterar.";
  if (effects.financial === "posted")
    return "O pedido tem contas lançadas. Estorne as contas antes de alterar.";
  if (base === "canceled") return "Pedido cancelado não pode ser alterado.";
  if (base === "done") return "Pedido concluído não pode ser alterado.";
  return null;
}

const NOTHING_TO_DO: Readonly<Record<OrderManualAction, string>> = {
  post_stock: "O estoque deste pedido já está lançado.",
  reverse_stock: "Não há estoque lançado nem reservado para estornar.",
  post_financial: "As contas deste pedido já estão lançadas.",
  reverse_financial: "Não há contas lançadas para estornar.",
};

/**
 * Etapas da ação manual pelo estado dos efeitos (mesmo plano do motor da T1.6): lançar estoque com reserva consome a
 * reserva; estornar estoque só reservado libera a reserva. Lançar em pedido cancelado = `action_not_allowed`; nada a
 * fazer = `action_not_applicable`.
 */
export function manualActionSteps(
  state: EffectState,
  action: OrderManualAction,
  base: BaseState,
): EffectStep[] {
  if (base === "canceled" && (action === "post_stock" || action === "post_financial"))
    fail("action_not_allowed", "Pedido cancelado não lança estoque nem contas.");
  const { steps } = planEffects(state, [action], base);
  if (steps.length === 0) fail("action_not_applicable", NOTHING_TO_DO[action]);
  return steps;
}

export type OrderDraftFields = {
  date: string;
  contactId: string;
  sellerId: string | null;
  priceListId: string | null;
  paymentTermId: string | null;
  carrierId: string | null;
  freightMode: SalesOrderDraft["freightMode"];
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  notes: string;
  items: {
    productId: string | null;
    description: string;
    unit: string | null;
    qtyQ4: number;
    listPriceCents: number;
    discountPct4: number;
  }[];
};

/** Rascunho da proposta (T1.7) → campos do pedido, com o preço de tabela congelado de cada item. */
export function draftToOrderFields(d: SalesOrderDraft): OrderDraftFields {
  return {
    date: d.date,
    contactId: d.contactId,
    sellerId: d.sellerId,
    priceListId: d.priceListId,
    paymentTermId: d.paymentTermId,
    carrierId: d.carrierId,
    freightMode: d.freightMode,
    discountPct4: d.discountPct4,
    discountCents: d.discountCents,
    freightCents: d.freightCents,
    otherExpensesCents: d.otherExpensesCents,
    notes: d.notes,
    items: d.items.map((it) => ({
      productId: it.productId,
      description: it.description,
      unit: it.unit,
      qtyQ4: it.qtyQ4,
      listPriceCents: it.listPriceCents,
      discountPct4: it.discountPct4,
    })),
  };
}
